import React, { useEffect, useCallback, useState } from "react";

import {
    Box,
    Paper,
    Divider,
    TextField,
    Grid,
    Typography,
    Switch,
    FormControlLabel,
    styled,
    Card,
    Select,
    MenuItem,
    FormControl,
    FormHelperText,
    InputLabel,
    Checkbox,
    ListItemText,
    OutlinedInput,
    Chip,
    List,
    ListItem
} from '@mui/material';

import {
    LoadingButton
} from '@mui/lab';

import { useNavigate, useParams } from "react-router-dom";
import { useFormik } from 'formik';
import CommonBreadcumbs from '../../common/CommonBreadcumbs';
import CommonSectionHeader from "../../common/CommonSectionHeader";
import CommonRestricted from '../../common/CommonRestricted';

import { mergeSchema } from "../../../utils/dataSchema"
import { spliceString } from "../../../utils/formatString";

import { useUsers, userTypes, targetTypes } from "../../../hooks/api/useUsers";
import { useLocations, locationTypes, targetTypes as locationTargetTypes } from '../../../hooks/api/useLocations';
import { useCustomFields, customFieldModules, customFieldFunctions } from '../../../hooks/api/useCustomFields';
import { useSystemPrompt } from "../../../hooks/useSystemPrompt";
import { useAppContextFeatures } from '../../../context/AppContext';

import {
    initialValues, initialPasswordValues,
    validationSchema, validationPasswordSchema
} from "../../../schema/users/workerSchema";
import { inputTypes, inputTypesDef } from "../../../schema/customFields/customFieldSchema"

import { useSystemRoutes, routeTypes } from '../../../hooks/useSystemRoutes';
import { useSystemHead, headTitles } from '../../../hooks/useSystemHead';
import { usePermissions, scopeIds, scopeTypes, functionTypes } from '../../../hooks/usePermissions';
import { useTranslations } from '../../../hooks/useTranslations';

// import UpdatePassword from "../common/UpdatePassword";
import { useStateCustomFields } from "../../../hooks/state/useStateCustomFields";
import { useAuthAccess } from "../../../hooks/useAuthAccess";

const StyledCard = styled(Card)(({ theme }) => ({
    padding: theme.spacing(2)
}));

export default function UpdateWorker({
    routeType,
    isOwnProfile = false
}) {
    const { t } = useTranslations({ ns: ["common", "@user"] })

    const { authData } = useAuthAccess()

    let { id } = useParams();

    // if the mode is only update own profile
    if (isOwnProfile) {
        id = authData?.profile?.user?.id
    }

    const { promptMessage } = useSystemPrompt();
    const { isLoading, get, update, filterOperatorTypes } = useUsers({ userType: userTypes.worker });
    const { isPermissionAllow } = usePermissions(routeType, scopeIds.user.id, scopeTypes.user.worker.id)
    const { list: listCustomFields } = useCustomFields({ customFieldModule: customFieldModules.user, customFieldFunction: customFieldFunctions.user.worker })

    const { list: listLocation } = useLocations({ locationType: locationTypes.location, targetType: targetTypes.location });

    const { setSystemLoading, openCustomDrawer } = useAppContextFeatures();

    const navigate = useNavigate();
    const [savedValues, setSavedValues] = useState(null)
    const { getRoute } = useSystemRoutes(routeType);
    const { getHtmlHead } = useSystemHead(routeType)
    const [dataLocations, setDataLocations] = useState([])
    // const [customFields, setCustomFields] = useState([])

    const [checked, setChecked] = useState(false);

    const {
        dataCustomFields,

        getInitialFormStateRows,
        getSubmitFormDataFromRows,

        getCustomFieldOptionNameByIds,

        RenderCustomFieldSelectionTextbox,
        RenderCustomFieldSelectionDropdown,
        RenderCustomFieldSelectionMultiDropdown
    } = useStateCustomFields(
        {
            fieldModule: customFieldModules.user,
            moduleFunction: customFieldFunctions.user.worker
        }
    )

    const getSchema = () => {
        if (checked === true) {
            return mergeSchema(validationSchema, validationPasswordSchema);
        }
        return validationSchema
    }

    const handleAllowChangePassword = (event) => {
        setChecked(event.target.checked);
    };

    const loadData = useCallback(async (id) => {

        setSystemLoading(true)

        try {

            const { data } = await get({ id, dataTypesArray: ["location"] })
            const { worker, custom_fields: savedCustomFields = [] } = data;

            const {
                locations,
            } = worker;

            const cf = await getInitialFormStateRows({
                savedCustomFields,
            })

            setSavedValues({
                username: worker.username,
                firstname: worker.firstname,
                lastname: worker.lastname,
                contactno: worker?.contact ? worker.contact : "",
                address: worker.address,
                email: worker?.email ? worker.email : "",
                status: worker?.status === '1',
                remark: worker.remark,
                locationId: locations.map(i => i.id),
                ...initialPasswordValues,
                rows: cf
            })

        } catch (error) {
            promptMessage(false, `${error?.message}`)
        }

        setSystemLoading(false)

    }, [id])

    const onSubmit = async (currentValues) => {

        try {

            const { rows } = values;

            let cf = getSubmitFormDataFromRows({ rows })

            let data = {
                credential: {
                    firstname: currentValues?.firstname,
                    lastname: currentValues?.lastname,
                    password: currentValues?.password,
                    contact: currentValues?.contactno ? currentValues.contactno : "",
                    email: currentValues?.email ? currentValues.email : "",
                    status: currentValues?.status ? 1 : 0,
                },
                worker: {
                    remark: currentValues?.remark ? currentValues.remark : ""
                },
                props: {
                    location_id: currentValues?.locationId
                },
                custom_fields: cf

            }
            const response = await update({ id, data, scopeType: "", targetType: targetTypes.worker });

            promptMessage(true, `${response?.message}`)

            if (isOwnProfile === false) {
                navigate(getRoute("users/workers"))
            }

        } catch (error) {
            promptMessage(false, `Action Failed. (${error?.message})`)
        }
    }

    const fetchLocationData = async () => {
        try {

            const props = {
                limit: -1,
                offset: 0,
                filterValue: [],
                sortInfo: {
                    id: "location_name",
                    dir: 0,
                }
            }

            props.locationType = locationTypes.location

            const { data } = await listLocation(props)

            setDataLocations(data?.locations)

        } catch (error) {
            promptMessage(false, `${error?.message}`)
        }
    }

    useEffect(() => {

        loadData(id);
        fetchLocationData()

    }, [])

    const formik = useFormik({
        initialValues: savedValues,
        validationSchema: getSchema(),
        enableReinitialize: true,
        onSubmit
    });

    const { values, errors, touched, handleChange, setFieldValue } = formik;

    const getLocationNameByIds = (optionIdArray) => {

        if (!optionIdArray) return []

        let optionNames = [];

        dataLocations.forEach((e) => {
            // e.options.forEach((e2) => {
            // if(e2.option_key === optionIdArray){
            if (optionIdArray.includes(e.id)) {
                optionNames.push(e.location_name)
            }
            // })
        })

        return optionNames
    }

    const RenderLocation = () => {

        return (
            <>
                <Grid item xs={12} sm={12} md={3} textAlign={"left"}>
                    <CommonSectionHeader
                        title={t('locationPermissions')}
                        subTitle={t('dataAccessScopes')}
                    />
                </Grid>


                <Grid item xs={12} sm={12} md={9}>
                    <FormControl
                        margin="dense"
                        fullWidth
                        size="small"
                        error={touched.locationId && Boolean(errors.locationId)}
                    >

                        <InputLabel id="location">{t('location')}</InputLabel>
                        <Select
                            disabled={
                                isOwnProfile
                            }
                            labelId="locationId"
                            label={t('location')}
                            name={"locationId"}
                            // onChange={(event) => {
                            //     setFieldValue(`locationId`, event.target.value)
                            // }}
                            onChange={handleChange}
                            value={values?.locationId ? values.locationId : []}
                            renderValue={(selected) => {

                                return (
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                        {
                                            getLocationNameByIds(selected).map((i, index) => (
                                                <Chip
                                                    key={index}
                                                    label={spliceString({ val: i, maxLength: 20 })}
                                                />
                                            ))
                                        }
                                    </Box>
                                )

                            }}
                            multiple
                            input={<OutlinedInput id="select-multiple-chip" label="Chip" />}
                        >
                            {
                                dataLocations?.map((i, index) => {
                                    return (
                                        <MenuItem key={index} value={i.id}>
                                            <Checkbox checked={values.locationId.indexOf(i?.id) > -1} />
                                            <ListItemText
                                                // primary={`${i.location_name}`} 
                                                primary={`${spliceString({ val: i.location_name, maxLength: 50 })}`}
                                            />
                                        </MenuItem>
                                    )
                                })
                            }
                        </Select>
                        <FormHelperText>
                            {touched.locationId && errors.locationId}
                        </FormHelperText>
                    </FormControl>
                </Grid>
            </>
        )

    }

    const RenderChangePassword = () => {

        if (checked === false) return <></>

        return (
            <>
                <Grid item xs={12} sm={12} md={6} textAlign={"left"}>
                    <TextField
                        margin="dense"
                        size="small"
                        fullWidth
                        name={"password"}
                        label={t('password')}
                        type="password"
                        variant="outlined"
                        value={values.password}
                        onChange={handleChange}
                        error={touched.password && Boolean(errors.password)}
                        helperText={touched.password && errors.password}
                    />
                </Grid>

                <Grid item xs={12} sm={12} md={6} textAlign={"left"}>
                    <TextField
                        margin="dense"
                        size="small"
                        fullWidth
                        name={"passwordconfirm"}
                        label={t('confirmPassword')}
                        type="password"
                        variant="outlined"
                        value={values.passwordconfirm}
                        onChange={handleChange}
                        error={touched.passwordconfirm && Boolean(errors.passwordconfirm)}
                        helperText={touched.passwordconfirm && errors.passwordconfirm}
                    />
                </Grid>
            </>
        )

    }

    const RenderCustomFieldSelection = () => {

        if (dataCustomFields.length <= 0) return <></>

        if (!values?.rows) return <></>

        const rows = values.rows

        return (
            <>
                <Grid item xs={12}>
                    <Divider sx={{ mt: 2, mb: 1 }} />
                </Grid>

                <Grid item xs={12} sm={12} md={3} textAlign={"left"}>
                    <CommonSectionHeader
                        title={t('customProperties')}
                        subTitle={t('provideCustomItemInformation')}
                    />
                </Grid>
                <Grid item xs={12} sm={12} md={9}>
                    <List>
                        {
                            values.rows.map((item, index) => {

                                const { rows } = values

                                let itemValues = rows.length ? rows[index] : {}

                                let {
                                    fieldType,
                                    fieldLabel,
                                    fieldKey
                                } = item

                                return (
                                    <ListItem
                                        key={index}
                                        divider
                                        disablePadding
                                        sx={{
                                            py: 1
                                        }}
                                    >
                                        <Grid container spacing={1}>
                                            <Grid item md={3} xs={12} sm={12} display={"flex"} flexDirection={"column"} justifyContent={"center"} alignItems={"center"} >
                                                <Chip
                                                    label={fieldLabel}
                                                    color='primary'
                                                    variant='outlined'
                                                    size='large'
                                                    sx={{
                                                        width: '100%',
                                                        // textAlign: 'left',
                                                        fontSize: 16,
                                                        fontWeight: 800
                                                    }}
                                                />
                                            </Grid>
                                            <Grid item md={9} xs={12} sm={12}>

                                                {
                                                    fieldType === inputTypesDef.textbox &&

                                                    RenderCustomFieldSelectionTextbox({
                                                        index,
                                                        item,
                                                        itemValues,
                                                        inputProps: {
                                                            fullWidth: true,
                                                            name: `rows.${index}.customValue`,
                                                            label: itemValues?.fieldLabel,
                                                            type: "text",
                                                            variant: "outlined",
                                                            value: itemValues?.customValue,
                                                            onChange: (event) => setFieldValue(`rows.${index}.customValue`, event.target.value),
                                                            // error: Boolean(itemErrors?.customValue),
                                                            // helperText: itemErrors?.customValue
                                                        }
                                                    })
                                                }

                                                {
                                                    fieldType === inputTypesDef.dropdown &&

                                                    RenderCustomFieldSelectionDropdown({
                                                        index,
                                                        item,
                                                        itemValues,
                                                        selectProps: {
                                                            labelId: "customValue",
                                                            label: itemValues?.fieldLabel,
                                                            name: `rows.${index}.customArray`,
                                                            onChange: (event) => {
                                                                setFieldValue(`rows.${index}.customArray`, [event.target.value])
                                                            },
                                                            size: 'small',
                                                            margin: 'dense',
                                                            value: itemValues.customArray.length ? itemValues.customArray[0] : ""
                                                        }
                                                    })
                                                }

                                                {
                                                    fieldType === inputTypesDef.multidropdown &&

                                                    RenderCustomFieldSelectionMultiDropdown({
                                                        index,
                                                        item,
                                                        itemValues,
                                                        selectProps: {
                                                            labelId: itemValues?.fieldLabel,
                                                            label: itemValues?.fieldLabel,
                                                            name: `rows.${index}.customArray`,
                                                            onChange: handleChange,
                                                            renderValue: (selected) => {

                                                                return (
                                                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                                                        {
                                                                            getCustomFieldOptionNameByIds(selected, dataCustomFields, fieldKey).map((i, index) => (
                                                                                <Chip
                                                                                    key={index}
                                                                                    // color={'red'}
                                                                                    label={spliceString({ val: i, maxLength: 20 })}
                                                                                />
                                                                            ))
                                                                        }
                                                                    </Box>
                                                                )

                                                            },
                                                            size: 'small',
                                                            margin: 'dense',
                                                            multiple: true,
                                                            input: <OutlinedInput id="select-multiple-chip" label="Chip" />,
                                                            value: itemValues?.customArray ? itemValues.customArray : []
                                                        }
                                                    })
                                                }

                                            </Grid>
                                        </Grid>
                                    </ListItem>
                                )

                            })
                        }

                    </List>

                </Grid>
            </>
        )
    }

    const RenderCredentialInfo = () => {
        return (
            <>
                <Grid item xs={12} sm={12} md={3} textAlign={"left"}>
                    <CommonSectionHeader
                        title={t('credential')}
                        subTitle={t('accessAuthentication')}
                    />
                </Grid>

                <Grid item xs={12} sm={12} md={9}>

                    <Grid container spacing={1}>
                        <Grid item xs={12} sm={12} md={6} textAlign={"left"}>
                            <TextField
                                disabled
                                margin="dense"
                                size="small"
                                fullWidth
                                name={"username"}
                                label={t('username')}
                                type="text"
                                variant="outlined"
                                value={values?.username}
                                onChange={handleChange}
                            // error={touched.username && Boolean(errors.username)}
                            // helperText={touched.username && errors.username}
                            />
                        </Grid>
                        <Grid item xs={12} sm={12} md={6} textAlign="left">
                            <FormControlLabel
                                control={
                                    <Switch
                                        disabled={
                                            isOwnProfile
                                        }
                                        sx={{
                                            ml: 2,
                                            my: 1
                                        }}
                                        name="status"
                                        checked={Boolean(values?.status)}
                                        onChange={(event, checked) => {
                                            setFieldValue("status", checked);
                                        }}
                                    />
                                }
                                label={
                                    Boolean(values.status) ? t('enabled') : t('disabled')
                                }
                            />
                        </Grid>

                        {
                            (isOwnProfile === false) &&
                            <>
                                <Grid item xs={12} sm={12} md={12} textAlign="left">

                                    <Divider>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    size="small"
                                                    onChange={handleAllowChangePassword}
                                                />
                                            }
                                            label={t('updatePassword')}
                                        />
                                    </Divider>

                                </Grid>

                                {
                                    RenderChangePassword()
                                }
                            </>
                        }

                    </Grid>
                </Grid>
            </>
        )
    }

    const RenderProfileInfo = () => {
        return (
            <>
                <Grid item xs={12} sm={12} md={3} textAlign={"left"}>
                    <CommonSectionHeader
                        title={t('workerProfile')}
                        subTitle={t('fullDetailsOfWorkerInformation')}
                    />
                </Grid>

                <Grid item xs={12} sm={12} md={9}>
                    {/* {JSON.stringify(values)} */}
                    <Grid container spacing={1}>
                        <Grid item xs={6}>
                            <TextField
                                margin="dense"
                                size="small"
                                fullWidth
                                name={"firstname"}
                                label={t('firstName')}
                                type="text"
                                autoFocus
                                variant="outlined"
                                value={values?.firstname}
                                onChange={handleChange}
                                error={Boolean(errors.firstname)}
                                helperText={errors?.firstname}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                margin="dense"
                                size="small"
                                fullWidth
                                name={"lastname"}
                                label={t('lastName')}
                                type="text"
                                variant="outlined"
                                value={values?.lastname}
                                onChange={handleChange}
                                error={Boolean(errors.lastname)}
                                helperText={errors?.lastname}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                margin="dense"
                                size="small"
                                fullWidth
                                name={"email"}
                                label={t('email')}
                                type="email"
                                variant="outlined"
                                value={values?.email}
                                onChange={handleChange}
                                error={Boolean(errors.email)}
                                helperText={errors?.email}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                margin="dense"
                                size="small"
                                fullWidth
                                name={"contactno"}
                                label={t('contact')}
                                type="text"
                                variant="outlined"
                                value={values?.contactno}
                                onChange={handleChange}
                                error={Boolean(errors.contactno)}
                                helperText={errors?.contactno}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                variant="outlined"
                                name={"remark"}
                                label={t('remark')}
                                margin="dense"
                                fullWidth
                                size="small"
                                rows={3}
                                multiline
                                value={values?.remark ? values.remark : ""}
                                onChange={handleChange}
                                error={Boolean(errors.remark)}
                                helperText={errors?.remark}
                            />
                        </Grid>
                    </Grid>
                </Grid>
            </>
        )
    }

    const RenderForm = () => {

        if (!values) return <></>

        return (

            <form onSubmit={formik.handleSubmit}>
                <StyledCard>
                    <Grid container spacing={1}>

                        {
                            RenderCredentialInfo()
                        }

                        <Grid item xs={12}>
                            <Divider sx={{ mt: 1, mb: 1 }} />
                        </Grid>

                        {
                            RenderProfileInfo()
                        }

                        <Grid item xs={12}>
                            <Divider sx={{ mt: 1, mb: 1 }} />
                        </Grid>

                        {
                            RenderLocation()
                        }

                        {
                            RenderCustomFieldSelection()
                        }

                    </Grid>

                </StyledCard>

                <Grid container spacing={1} sx={{ pl: 2, pr: 2 }}>
                    <Grid item md={12} textAlign={"right"}>
                        <LoadingButton
                            type="submit"
                            // fullWidth
                            variant="contained"
                            sx={{
                                mt: 3,
                                mb: 2
                            }}
                            loading={isLoading}
                        >
                            {t('saveChanges')}
                        </LoadingButton>
                    </Grid>

                </Grid>
            </form>

        )
    }

    // const getBreadcumbActionMenus = () => {

    //     let props = []

    //     if (isPermissionAllow({ functionType: functionTypes.pageSettings.id })) {
    //         props.push({
    //             displayText: "Update Password",
    //             onClick: () => {

    //                 openCustomDrawer({
    //                     show: true,
    //                     anchor: "right",
    //                     title: `Change Password`,
    //                     PaperProps: {
    //                         sx: { width: 2 / 3 }
    //                     },
    //                     // handleOnClose: () => setPreviewPdfOpen(false),
    //                     ComponentActions: <></>,
    //                     ComponentBody:
    //                         <UpdatePassword
    //                             routeType={routeType}
    //                             userId={id}
    //                             userType={userTypes.worker}
    //                         />
    //                 })

    //             }
    //         })
    //     }

    //     return props

    // }

    const getBreadcumbs = () => {
        if (isOwnProfile) {
            return [
                {
                    displayText: t('updateProfile'),
                    props: {
                        color: 'secondary.main'
                    }
                },
            ]
        }

        return [
            {
                displayText: t('workers'),
                props: {
                    href: getRoute("users/workers")
                }
            },
            {
                displayText: t('updateWorker'),
                props: {
                    color: 'secondary.main'
                }
            },
        ]
    }

    return (
        <Box>
            {
                getHtmlHead({ routeType, title: t('updateWorker') })
            }

            <CommonBreadcumbs
                routeType={routeType}
                pageTitle={
                    isOwnProfile ? t(`updateProfile`) : t('updateWorker')
                }
                // actionMenus={
                //     getBreadcumbActionMenus()
                // }
                breadcrumbs={
                    getBreadcumbs()
                }
            />


            {
                (isPermissionAllow({ functionType: functionTypes.pageUpdate.id }) || isOwnProfile === true) ?
                    RenderForm()
                    :
                    <CommonRestricted />
            }

        </Box>
    )
}